export const meta = {
  id: '35-cuisine',
  order: 35,
  checkFocus: { numbers: ['pc'] },   // what the end-of-lesson check tests first
  titleFi: 'Keittiö',
  titleEn: 'Cuisine',
  duolingoUnit: 'duo-41-cuisine',
  // + Sanapaja's own words this lesson first needs (docs/COURSE.md "Own words in the lessons"; waves/wave-NN/lessons.json)
  lemmaIds: ['mentys', 'mentyn', 'lēdegon', 'māvos', 'rizmon', 'dohaeragon', 'ours:ibardutir', 'ours:gīmīnnon', 'ours:qepēbilliapos', 'ours:dokerta', 'ours:parklilla', 'ours:sōluttiapos', 'ours:kisalbrenka', 'ours:bāngilla', 'ours:desserti', 'ours:patata', 'ours:sokolati', 'ours:kanella', 'ours:dilli', 'ours:silli', 'ours:qepiapos', 'ours:mustikka', 'ours:grilli', 'ours:viski', 'ours:sampanja', 'ours:martini', 'ours:vodka', 'ours:litra'],
  exampleIds: [
    'ex-1e8b0b3eef94',
    'ex-1f8e8858fbf4',
    'ex-7753c9b519e2',
    'hotd-s1e08-001',
    'ex-7efa15668b36',
    'hotd-s3e08-003',
    'ex-1855e6ae151d',
    'ex-7f775dec21ad',
    'ex-8d6422680a6b',
    'ex-83f60a37f3e0',
    'ex-4167b8d9b26e',
  ],
  sources: [
    'https://wiki.languageinvention.com/index.php?title=Appendix:High_Valyrian_Duolingo_Tips_and_Notes',
    'https://wiki.languageinvention.com/index.php?title=High_Valyrian_Grammar',
    'https://wiki.languageinvention.com/index.php?title=Mentyn',
    'https://wiki.languageinvention.com/index.php?title=Lēdegon',
    'https://wiki.languageinvention.com/index.php?title=Dohaeragon',
    'https://wiki.languageinvention.com/index.php?title=Parklun',
    'https://wiki.languageinvention.com/index.php?title=Drōmun',
    'https://wiki.languageinvention.com/index.php?title=Jurneryn',
  ],
  // the week (docs/COURSE.md "Lesson hub")
  grammarAlso: ['numbers'],
  drills: { numbers: ['pc', 'pcpl'] },
  showLines: ['hotd-s1e08-001', 'hotd-s3e08-003'],
  pronunciation: { focus: 'diphthongs', words: ['dohaeragon', 'vāedagon', 'qaedar', 'jorrāeliarza', 'jaelarys'] },
  letter: {
    fi: 'Kirjoita juhla-aterian ruokalista: muutama ja kollektiivi.',
    en: 'Write the menu of a feast: paucal and collective.',
  },
  scene: 'hotd-s3e08-1',
}

# Keittiö

Valyrialaisessa keittiössä ruukut täytetään, vieraita kestitään ja tomaatteja viipaloidaan muutama kerrallaan. Tämä oppitunti nostaa pöytään luvun, jota suomessa ei ole: **muutaman** (paukaalin). Lisäksi opit kaksi keittiöverbiä, jotka valitsevat sijansa omalla tavallaan.

## Neljä lukua

<LessonLink n={28}>Oppitunnilla 28</LessonLink> kävit läpi valyrian neljä lukua. Kerrataan ne, koska nyt yksi niistä otetaan kunnolla käyttöön:

- **yksikkö**: täsmälleen yksi
- **kollektiivi**: kaikki, joukko kokonaisuutena
- **muutama** (paukaali): pieni, epämääräinen joukko
- **monikko**: monta

Tips & Notes vertailee näin:

- <Hv glossed>melvo</Hv> – ’(yksi) päärynä’
- <Hv glossed>melva</Hv> – ’päärynät’
- <Hv glossed>melvun</Hv> – ’muutama päärynä, joitakin päärynöitä’

Englanniksi muutaman voi kääntää monella tavalla (*a few, some, several*), ja Tips & Notes neuvoo pysymään yhdessä käännöksessä. Suomeksi luonteva vastine on useimmiten *muutama* tai *joitakin*.

<Compare fi="päärynö|itä" hv="melv|un" label="joitakin päärynöitä ~ muutama" />

Muutama on keittiön luku: otetaan muutama tomaatti, kaadetaan vähän maitoa.

<Example id="ex-1e8b0b3eef94" />

<Example id="ex-1f8e8858fbf4" />

Ensimmäisessä <Hv glossed>gerpuni</Hv> on sanan <Hv>gerpa</Hv> muutaman akkusatiivi, toisessa <Hv glossed>jūlruni</Hv> sanan <Hv glossed>jūlor</Hv> ’maito’.

## Muutaman taivutus

Muutaman tunnistaa päätteestä *-un*, *-yn*, *-ȳn* tai *-in*. Sen perään tulevat sijapäätteet, jotka ovat kaikilla substantiiveilla samat teemavokaalia lukuun ottamatta. Tässä <Hv glossed>vala</Hv> ’mies’:

<Paradigm lemma="vala" numbers="pc" />

Ja vertailun vuoksi <Hv glossed>anne</Hv> ’hevonen’:

<Paradigm lemma="anne" numbers="pc" />

Tips & Notes nostaa esiin kolme asiaa, ja ne näkyvät taulukoista suoraan:

1. **Instrumentaali ja vokatiivi ovat muutamassa aina samat**: <Hv>valussa</Hv>, <Hv>annisse</Hv>.
2. **Nominatiivi, akkusatiivi ja genetiivi muodostetaan aina samoin**: muutaman pääte, akkusatiivissa lisäksi *-i* ja genetiivissä *-o*: <Hv fi="muutama mies (nominatiivi, akkusatiivi, genetiivi)">valun, valuni, valuno</Hv>.
3. **Genetiivin jälkeisissä sijoissa loppuvokaali määräytyy sanan teemavokaalin mukaan.** Teemavokaali on yleensä perusmuodon viimeinen vokaali, eikä suku ratkaise sitä: *a*-sanat saavat *a*:n (<Hv>valunta</Hv>), *y*-sanat *y*:n (<Hv l="mentys">mentynty</Hv>), *o*-sanat *o*:n (<Hv>vēzunto</Hv>), *e*-sanat *e*:n (<Hv>anninte</Hv>, myös aurinkosukuinen <Hv>zaldrīzinte</Hv>) ja *i*-sanat *i*:n (vesisukuinen <Hv>ambrinti</Hv>).

Suomalaiselle kolmas kohta muistuttaa vähän vokaalisointua: pääte pysyy samana, ja vain sen vokaali vaihtuu sanan mukaan. Valyriassa vokaali ei kuitenkaan määräydy sanan muiden vokaalien vaan sen <Term id="deklinaatio">taivutusluokan</Term> mukaan. Tämän oppitunnin oma sana <Hv glossed>mentys</Hv> ’sotilas’ on aurinkosukuinen *y*-sana, joten sen muutamassa vokaali on y: <Hv glossed>mentyn, mentyni, mentyno, mentynty</Hv>.

Jos taivutus tuntuu tutulta, se johtuu <LessonLink n={28}>oppitunnista 28</LessonLink>: siellä taivutettiin <Hv glossed>olvȳn</Hv> ’moni, paljon’ täsmälleen samalla kaavalla.

<Note>
**Nyrkkisääntö muutaman perusmuodolle** Damien Halmelta (Academy of High Valyrian): sanan loppuun tulee *n*. Viimeinen *e* muuttuu *i*:ksi (<Hv>gelte</Hv> → <Hv l="gelte">geltin</Hv>), *a* ja *o* yleensä *u*:ksi (<Hv>vala</Hv> → <Hv>valun</Hv>, <Hv>tegon</Hv> → <Hv l="tegon">tegun</Hv>), *y* pysyy (<Hv>loktys</Hv> → <Hv>loktyn</Hv>). *r*-loppuinen sana saa *-un* (<Hv>embar</Hv> → <Hv>embrun</Hv>), ja *-ion* muuttuu *-ȳn*:ksi (<Hv>dārion</Hv> → <Hv l="dārion">dārȳn</Hv>). Sanakirjan taulukko ratkaisee aina.
</Note>

<Pronounce word="melvun" />
<Pronounce l="mentys" word="mentyni" />

## Muutama on kieliopillisesti monikko

Tämä kohta on tärkein, ja juuri tässä suomi johtaa harhaan. Suomessa *muutama* on kieliopillisesti yksikkö: *Muutama mies laulaa.* Valyriassa muutama käyttäytyy kuin monikko:

- kun muutama on subjektina, **verbi on monikossa**,
- kun siihen viitataan pronominilla, käytetään **monikon pronominia** (3. persoonassa <Hv>pōnta</Hv>),
- ja wikin kielioppisivun mukaan myös adjektiivit kohtelevat muutamaa monikkona.

Tämä on kollektiivin peilikuva: kollektiivi on merkitykseltään joukko, mutta adjektiivi ja verbi <Term id="mukautuminen">mukautuvat</Term> siihen kuin yksikköön; muutama päinvastoin.

<Example id="ex-7753c9b519e2" />

<Interlinear hv="Kesa parkl|un dōrior lopon aem|is." gloss="tämä-TERR.NOM.PL annos-NOM.PC ei.yhtään-TERR.ACC suola-ACC omistaa-PRS.3PL" fi="Tässä lihapalassa ei ole suolaa." />

<Hv l="parklun">Parklun</Hv> on muutamassa, joten demonstratiivi on maasukuisen monikon muodossa <Hv l="kesa">kesa</Hv> ja verbi monikon 3. persoonassa <Hv>aemis</Hv>, vaikka suomeksi puhutaan yhdestä lihapalasta. Miksi yhdestä? Siitä seuraavaksi.

## Muutamasta uusi sana

Joskus muutama irtoaa omaksi sanakseen. Tips & Notesin esimerkki on tämän oppitunnin sanapari: <Hv glossed>mentys</Hv> on ’sotilas’, mutta <Hv glossed>mentyn</Hv> kääntyy parhaiten ’osasto, joukkue, falangi’. Pieni joukko sotilaita on muuttunut yhdeksi yksiköksi.

Kun tällainen sana vakiintuu, sen voi taas panna monikkoon. Syntyy **muutaman monikko**, jonka päätteet ovat aina samat:

<Paradigm lemma="mentyn" numbers="pcpl" />

Päätteet näyttävät tutuilta: ne muistuttavat monikon päätteitä (*-i, -ī, -oti*) muutaman *n*:n perässä. Tips & Notes huomauttaa vielä, että sanoilla, joiden teemavokaali on i, muutaman ja muutaman monikon instrumentaali ja komitatiivi ovat samat.

Sanakirjasta löytyy useita tällaisia sanoja, ja moni niistä liittyy keittiöön tai sarjoihin:

- <Hv glossed>parklon</Hv> ’liha’ → <Hv glossed>parklun</Hv> ’lihapala, annos’
- <Hv glossed>drōmon</Hv> ’muna’ → <Hv glossed>drōmun</Hv> ’(munien) pesue’
- <Hv glossed>tīkos</Hv> ’sulka’ → <Hv glossed>tīkun</Hv> ’siipi’ (muutama sulka = siipi)
- <Hv glossed>jurnerys</Hv> ’vartija’ → <Hv glossed>jurneryn</Hv> ’vartio’ (pieni joukko vartijoita)

<Note>
Damien Halme (Academy of High Valyrian) poimii sanakirjasta lisää samanlaisia:

- <Hv glossed>guēse</Hv> ’puu’ → <Hv glossed>guēsin</Hv> ’metsä’ (muutama puu)
- <Hv glossed>lenton</Hv> ’talo’ → <Hv glossed>lentun</Hv> ’yhteisö, naapurusto’
- <Hv glossed>gēlion</Hv> ’hopea’ → <Hv glossed>gēlȳn</Hv> ’velka, hinta’. Wikissä <Hv l="gēlȳn">gēlȳn</Hv> on sekä hopean muutama että oma sanansa; yhteys on Halmen tulkinta.
</Note>

Daemon tuo uutisen Syraxin munista. <Hv glossed>Drōmuni</Hv> on sanan <Hv glossed>drōmun</Hv> ’pesue’ akkusatiivi:

<Line id="hotd-s1e08-001" />

<Interlinear hv="Syraks drōm|uni arliar mādas." gloss="Syrax-NOM pesue-ACC.PC uusi-TERR.ACC.PL tuoda-PRF.3SG" fi="Syrax toi uuden pesueen." />

Adjektiivi <Hv>arliar</Hv> on taas monikossa, koska pesue on kieliopillisesti muutamassa. Westerosin sananlasku korpeista näyttää saman ilmiön: <Hv glossed>tīkuni</Hv> on sanan <Hv glossed>tīkun</Hv> muutaman monikko ’siivet’:

<Example id="ex-7efa15668b36" />

Muistatko tämän sananlaskun <LessonLink n={20}>oppitunnilta 20</LessonLink>? Silloin opettelit sen kokonaisena. Nyt näet, miksi siivet ovat <Hv l="tīkun" glossed>tīkuni</Hv>: <Hv glossed>tīkun</Hv> ’siipi’ on alun perin sanan <Hv glossed>tīkos</Hv> ’sulka’ muutama, joten sen monikko on muutaman monikko.

<Line id="hotd-s3e08-003" />

Tips & Notes muistuttaa kuitenkin, että tällaisia uudelleen tulkittuja sanoja on vähän. Useimmiten muutama tarkoittaa yksinkertaisesti ’muutama’.

<Pronounce l="mentyn" word="mentyn" />

## Täyttäminen: lēdegon ja genetiivi

<Hv glossed>Lēdegon</Hv> ’täyttää’ on oikukas: aine, jolla astia täytetään, on **genetiivissä**. Täytettävä astia on tavallinen akkusatiiviobjekti.

<Example id="ex-1855e6ae151d" />

<Interlinear hv="Māvos rizm|o lēdet|an." gloss="ruukku-ACC hiekka-GEN täyttää-PRF.1SG" fi="Täytin ruukun hiekalla." />

Tips & Notes sanoo erikseen, että instrumentaali olisi tässä väärin: ei <Hv>rizmoso</Hv> vaan <Hv>rizmo</Hv>. Suomalaisesta instrumentaali tuntuisi luontevalta, koska suomeksi sanotaan *hiekalla*, ja adessiivin välinemerkitys vastaa valyrian instrumentaalia. Juuri siksi tämä kannattaa painaa mieleen.

<Compare fi="hieka|lla" hv="rizm|o" label="täyttää jollakin: adessiivi ~ genetiivi" />

Muistisääntönä voi käyttää suomen ilmausta *täynnä hiekkaa*: täyte on astian sisältöä, ei työkalu. Vertaa verbiin, jonka kanssa sama hiekka on väline. <Hv glossed>Kisikagon</Hv> ’syöttää’ ottaa syötettävän ruoan instrumentaalissa, ja siksi tässä on <Hv>rizmoso</Hv>:

<Example id="ex-7f775dec21ad" />

Sanakirjan mukaan <Hv glossed>lēdegon</Hv> tarkoittaa myös ’tukkia, sulkea’. Silloinkin tukittava on akkusatiivissa ja se, millä tukitaan, genetiivissä. Alla olevassa esimerkissä näkyy vain tukittava tunneli (<Hv l="lōrty">lōrti</Hv>):

<Example id="ex-8d6422680a6b" />

<Pronounce word="lēdegon" />

## Tarjoileminen: dohaeragon

<Hv glossed>Dohaeragon</Hv> ’palvella, tarjoilla’ jakaa sijat toisin kuin englannin *serve food*:

- **suora objekti on ihminen, jota palvellaan** (akkusatiivi),
- **tarjoiltava asia on instrumentaalissa**.

Tässä suomi auttaa, kunhan valitset oikean verbin. Suomen *kestitä* toimii täsmälleen samoin: *kestitsimme vieraita ruoalla*. Objektina on vieras, ja ruoka on välineen sijassa, suomessa adessiivissa ja valyriassa instrumentaalissa. Ajattele siis <Hv>dohaeragon</Hv>-verbiä kestitsemisenä, niin sijat asettuvat oikein.

<Compare l="havor" fi="ruoa|lla" hv="hav|orzo" label="kestitä jollakin: adessiivi ~ instrumentaali" />

<Example id="ex-83f60a37f3e0" />

<Hv glossed>Havorzo</Hv> on sanan <Hv glossed>havor</Hv> ’ruoka’ instrumentaali. Palveltavaa ihmistä ei tässä mainita, mutta ruoka on silti instrumentaalissa.

<Pronounce word="dohaeragon" />

Lopuksi keittiön astia, <Hv glossed>māvos</Hv> ’ruukku, kannu’, arkisessa pyynnössä:

<Example id="ex-4167b8d9b26e" />

## Harjoituslauseita

<Practice hv="Valun vāedis." fi="Muutama mies laulaa." />

<Practice hv="Melvuni ipradan." fi="Syön muutaman päärynän." />

<Practice hv="Māvos jūlro lēdetan." fi="Täytin ruukun maidolla." />

<Practice hv="Zentī havorzo dohaertan." fi="Kestitsin vieraita ruoalla." />

Ensimmäisessä verbi on monikossa (<Hv l="vāedagon">vāedis</Hv>), vaikka suomessa se on yksikössä. Kolmannessa maito on genetiivissä (<Hv>jūlro</Hv>), neljännessä vieraat ovat akkusatiivissa (<Hv>zentī</Hv>) ja ruoka instrumentaalissa.

<Note>
**Kenen mukaan adjektiivi taipuu?** Damien Halmen (Academy of High Valyrian) harjoituslauseet, korjattuina sanakirjan muotoihin. Ensimmäisessä *vapaa* kuuluu kylään: <Hv glossed>lentor</Hv> ’kylä’ on maasukuinen kollektiivi, joten adjektiivikin on maasuvun genetiivissä. Toisessa <Hv glossed>azantyr</Hv> ’armeija’ on aurinkosukuisen sanan <Hv>azantys</Hv> kollektiivi: adjektiivi on aurinkosuvussa ja verbi yksikössä.

<Practice hv="Zokli aderi lentondo dāerȳro issarossa arghusi." fi="Nopeat sudet metsästävät vapaan kylän ihmisiä." />

<Practice hv="Azantyr kostōbys geltī kempī sindis." fi="Vahva armeija ostaa raskaita kypäriä." />
</Note>

## Sanat

<Vocab />

<Check />
